import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { reduxForm } from 'redux-form';

import { ApiLogOutAllDevices } from '../../../actions/Api';
import { closeModal, openModal } from '../../../actions/Modal';
import {
  clearPasswordForm,
  clearPasswordFormError,
  updateAccountInfo,
  updateContentSettings,
} from '../../../actions/UserAccount';
import Form from '../../../components/Form/Form';
import TextInput from '../../../components/lib/TextInput/TextInput';
import { AUTH_INVALID_USERNAME_PASSWORD } from '../../../constants/Anon';
import QA from '../../../constants/QATags';
import { FieldNames, messages as m } from '../../../constants/UserSettings';
import { getContentSettingsPath, getInfoPath } from '../../../lib/RouteUtils';
import Button from '../../Button/Button';
import { ModalTitle } from '../../Modal/Modal';

import '../UserSettings.scss';
import './PasswordModalBody.scss';

export const PasswordModalBody = injectIntl(
  class extends React.Component {
    static propTypes = {
      path: PropTypes.string,
      updateFail: PropTypes.bool,
      updateFailType: PropTypes.string,
      showGeneralErrorMessage: PropTypes.bool,
      showPasswordMessage: PropTypes.bool,
      updateSuccess: PropTypes.bool,
      handleSubmit: PropTypes.func.isRequired,
      openModal: PropTypes.func.isRequired,
      closeModal: PropTypes.func.isRequired,
      onAuthenticated: PropTypes.func,
      authenticationSettings: PropTypes.obj,
      updateAccountInfo: PropTypes.func.isRequired,
      updateContentSettings: PropTypes.func.isRequired,
      showLogOutAllDevicesCheckbox: PropTypes.bool,
      clearPasswordForm: PropTypes.func.isRequired,
      clearPasswordFormError: PropTypes.func.isRequired,
      fields: PropTypes.shape({
        currentPassword: PropTypes.shape({
          value: PropTypes.string,
          active: PropTypes.bool,
          error: PropTypes.string,
        }),
      }).isRequired,
      intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
      }).isRequired,
      escKeyPressed: PropTypes.bool,
      logOutAllDevices: PropTypes.func.isRequired,
      logOutAllDevicesSuccess: PropTypes.bool,
      logOutAllDevicesFail: PropTypes.bool,
    };

    state = {
      isLogOutAllDevicesChecked: false,
    };

    componentDidUpdate(prevProps) {
      const { updateSuccess, escKeyPressed, logOutAllDevicesSuccess } = this.props;
      const { isLogOutAllDevicesChecked } = this.state;
      const success = isLogOutAllDevicesChecked
        ? updateSuccess && logOutAllDevicesSuccess
        : updateSuccess;
      const prevSuccess = isLogOutAllDevicesChecked
        ? prevProps.updateSuccess && prevProps.logOutAllDevicesSuccess
        : prevProps.updateSuccess;

      if (success && !prevSuccess) {
        this.props.closeModal();
        if (this.props.onAuthenticated) {
          this.props.onAuthenticated();
        }
        this.openSuccessModal();
      }
      if (this.props.showPasswordMessage && prevProps.fields.currentPassword.active) {
        this.props.clearPasswordFormError();
      }
      if (!prevProps.escKeyPressed && escKeyPressed !== prevProps.escKeyPressed) {
        this.onCancel();
      }
    }

    componentWillUnmount() {
      this.props.clearPasswordForm();
    }

    openSuccessModal = () => {
      this.props.openModal({
        modalType: 'ModalCoachMark',
        body: this.props.intl.formatMessage(m.updateAccountSuccess),
      });
    };

    onCancel = () => {
      this.props.closeModal();

      if (!this.props.showGeneralErrorMessage) {
        this.props.openModal({
          modalType: 'ModalCoachMark',
          title: '',
          body: <FormattedMessage {...m.changesNotSaved} />,
          buttons: [],
        });
      }
    };

    onSubmit = () => {
      if (this.state.isLogOutAllDevicesChecked) {
        this.props.logOutAllDevices({ password: this.props.fields.currentPassword.value });
      }
      switch (this.props.path) {
        case getInfoPath():
          return this.props.updateAccountInfo(this.props.fields.currentPassword.value);
        case getContentSettingsPath():
          return this.props.updateContentSettings(
            this.props.fields.currentPassword.value,
            this.props.authenticationSettings,
          );
      }
    };

    getFailedUpdateErrors = () => {
      const msg = m[this.props.updateFailType];

      if (!msg) return null;

      return <FormattedMessage {...msg} />;
    };

    handleCheckboxChange(event) {
      this.setState({ isLogOutAllDevicesChecked: !this.state.isLogOutAllDevicesChecked });
    }

    renderGeneralError = () => {
      const failedMessage = this.getFailedUpdateErrors();

      if (this.props.showGeneralErrorMessage) {
        return (
          <div className='PasswordModalBody__general_error'>
            <span>{failedMessage}</span>
            <span>
              <FormattedMessage {...m.passwordModalGeneralError} />
            </span>
          </div>
        );
      }
      return null;
    };

    render() {
      const {
        updateFail,
        handleSubmit,
        fields: { currentPassword },
        intl: { formatMessage: t },
        showPasswordMessage,
        showLogOutAllDevicesCheckbox,
      } = this.props;

      const inputWrapperClassnames = classNames({
        PasswordModalBody__row: true,
        'UserSettings__input--error': !!updateFail,
        'LogOutAllDevices_checkbox--error':
          showLogOutAllDevicesCheckbox && !!updateFail && !currentPassword.active,
      });

      if (!currentPassword.active) {
        currentPassword.error = showPasswordMessage ? t(m.passwordModalIncorrectPasswordError) : '';
      } else {
        currentPassword.error = '';
      }

      const instructions = showLogOutAllDevicesCheckbox
        ? t(m.passwordModalNewPasswordInstructions)
        : t(m.passwordModalInstructions);
      const logOutAllDevicesCheckbox = (
        <div className='PasswordModalBody_logOutAllDevicesCheckboxRow'>
          <input
            type='checkbox'
            className='PasswordModalBody_checkbox'
            value={this.state.isLogOutAllDevicesChecked}
            onChange={() => {
              this.setState({ isLogOutAllDevicesChecked: !this.state.isLogOutAllDevicesChecked });
            }}
          />
          <p className='PasswordModalBody_checkboxLabel'>{t(m.passwordModalLogOutAllDevices)}</p>
        </div>
      );

      return (
        <Form className='PasswordModalBody' onSubmit={handleSubmit(this.onSubmit)}>
          <ModalTitle>{t(m.passwordModalTitle)}</ModalTitle>
          <p className='PasswordModalBody__instructions'>{instructions}</p>
          {this.renderGeneralError()}
          <div className={inputWrapperClassnames}>
            <TextInput
              type='password'
              placeholder={t(m.passwordModalInputPlaceholder)}
              {...currentPassword}
            />
          </div>
          {this.props.showLogOutAllDevicesCheckbox ? logOutAllDevicesCheckbox : null}
          <div className='PasswordModalBody__buttons'>
            <Button classes={['Button--white']} onClick={this.onCancel} qaTag={QA.CB}>
              {t(m.cancel)}
            </Button>
            <Button type='submit' className='Button' qaTag={QA.SUBB}>
              {t(m.submit)}
            </Button>
          </div>
        </Form>
      );
    }
  },
);

export function mapStateToProps(state) {
  const updateFailType = state.userAccount.get('updateFail');
  const updateFail = !!updateFailType;
  const showPasswordMessage = updateFailType === AUTH_INVALID_USERNAME_PASSWORD;
  const showGeneralErrorMessage = updateFail && !showPasswordMessage;
  const updateSuccess = state.userAccount.get('updateSuccess');
  const { locationBeforeTransitions } = state.routing;
  const path = locationBeforeTransitions ? locationBeforeTransitions.pathname : '';
  const logOutAllDevicesFail = state.userAccount.get('logOutAllDevicesFail');
  const logOutAllDevicesSuccess = state.userAccount.get('logOutAllDevicesSuccess');

  return {
    path,
    updateFail,
    updateSuccess,
    showPasswordMessage,
    showGeneralErrorMessage,
    updateFailType,
    logOutAllDevicesFail,
    logOutAllDevicesSuccess,
  };
}

export default reduxForm(
  {
    form: 'UserSettings_AccountInfo',
    fields: Object.values(FieldNames),
    destroyOnUnmount: false,
  },
  mapStateToProps,
  {
    openModal,
    closeModal,
    updateAccountInfo,
    updateContentSettings,
    clearPasswordForm,
    clearPasswordFormError,
    logOutAllDevices: ApiLogOutAllDevices,
  },
)(PasswordModalBody);
